import React, { useState, useEffect } from 'react';
import Select from "react-select";
import { useDispatch, useSelector } from 'react-redux';
import Draggable from 'react-draggable';
import {
  Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane, Button
} from 'reactstrap';
import classnames from 'classnames';
import Loader from 'feather-icons-react/build/IconComponents/Loader';
import { createAudience } from '../../../../slices/veloxity/audience/CreateReducer';
import { createAudienceDefinePreview } from '../../../../slices/veloxity/audience/CreateAudienceDefinePreview';
import { defineCreateAudience } from '../../../../slices/veloxity/audience/CreateDefineReducer';
import { audienceDefinePreviewSlice } from '../../../../slices/veloxity/audience/CreateAudienceDefinePreview';
import { findAllVeloxityAudience } from '../../../../slices/veloxity/audience/FindAllAudienceReducer';
import { getAudienceProfileTypes } from '../../../../slices/veloxity/audience/AudienceProfileTypes';
import { getProducts } from '../../../../slices/veloxity/audience/GetProductsReducer';
import AudiencePreviewModal from '../../../../components/modal/audience/AudiencePreviewModal';
import AudienceManuallyPreviewModal from '../../../../components/modal/audience/AudienceManuallyPreviewModal';
import CreateManuallyForm from './CreateManuallyForm';
import CreateWithAIForm from './CreateWithAIForm';

const standardAudiences = [
  { name: 'Mixed Android', osType: 'ANDROID', userStatus: 'MIXED' },
  { name: 'Mixed iOS', osType: 'IOS', userStatus: 'MIXED' },
  { name: 'Mixed All', osType: 'MIXED', userStatus: 'MIXED' },
  { name: 'Active Android', osType: 'ANDROID', userStatus: 'ACTIVE' },
  { name: 'Active iOS', osType: 'IOS', userStatus: 'ACTIVE' },
  { name: 'Active All', osType: 'MIXED', userStatus: 'ACTIVE' },
  { name: 'Inactive Android', osType: 'ANDROID', userStatus: 'INACTIVE' },
  { name: 'Inactive iOS', osType: 'IOS', userStatus: 'INACTIVE' },
  { name: 'Inactive All', osType: 'MIXED', userStatus: 'INACTIVE' }
];

const AudienceCreate = ({ isOpen, toggle, updateLoading, previewLoading }) => {
  const [selectedStandardAudience, setSelectedStandardAudience] = useState(null);
  const [activeTab, setActiveTab] = useState('1');
  const subscriptions = useSelector(state => state.getPermissionsList.subscriptions);

  const [createAudienceName, setCreateAudienceName] = useState('');
  const [defineAudienceName, setDefineAudienceName] = useState('');
  const [defineAudienceText, setDefineAudienceText] = useState('');

  const [profileTypeOptions, setProfileTypeOptions] = useState([]);
  const [selectedPersonaType, setSelectedPersonaType] = useState(null);
  const [existingNames, setExistingNames] = useState([]);

  const dispatch = useDispatch();
  const isLoading = useSelector(state => state.audienceCreate.loading);
  const operationStatus = useSelector(state => state.audienceCreate.status);
  const isLoadingDefine = useSelector(state => state.defineAudienceCreate.loading);
  const previewError = useSelector(state => state.audienceDefinePreview.error);

  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const oneYearAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(oneYearAgo);
  const [endDate, setEndDate] = useState(today);

  const [popup, setPopup] = useState(null);

  const closePopup = () => setPopup(null);

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const hasFeature = (feature) => {
    const audienceSubscription = subscriptions?.find(sub => sub.subscriptionType === "AUDIENCE");
    return audienceSubscription?.featureFlags?.[feature] === true;
  };

  useEffect(() => {
    if (hasFeature("manuel")) {
      setActiveTab('1');
    } else if (hasFeature("define")) {
      setActiveTab('2');
    } else if (hasFeature("predefined")) {
      setActiveTab('3');
    }
  }, [subscriptions]);
  

  useEffect(() => {
    const fetchProfileTypes = async () => {
      try {
        const iosResponse = await dispatch(getAudienceProfileTypes('IOS')).unwrap();
        const androidResponse = await dispatch(getAudienceProfileTypes('ANDROID')).unwrap();

        const iosOptions = iosResponse.items.map(item => ({ value: item.code, label: item.name }));
        const androidOptions = androidResponse.items.map(item => ({ value: item.code, label: item.name }));

        setProfileTypeOptions({
          ios: iosOptions,
          android: androidOptions
        });
      } catch (error) {
        console.error('Error fetching profile types:', error);
      }
    };

    fetchProfileTypes();
  }, [dispatch]);


  useEffect(() => {
    if (operationStatus === 'succeeded' || operationStatus === 'failed') {
      dispatch(findAllVeloxityAudience(false));
    }
  }, [operationStatus, dispatch]);

  useEffect(() => {
    const loadExistingNames = async () => {
      const audiences = await dispatch(findAllVeloxityAudience(false)).unwrap();
      const names = audiences.map(audience => audience.name);
      setExistingNames(names);
    };

    loadExistingNames();
  }, [dispatch]);


  const handleConfirmSubmit = async () => {
    updateLoading(true);

    const nameInput = previewData.name;
    const osType = previewData.osType;
    const userStatus = previewData.userStatus;
    const startDate = previewData.timeRange.startDate.split('T')[0];
    const endDate = previewData.timeRange.endDate.split('T')[0];
    const operationTypeValue = previewData.operationType;

    const audienceData = {
      name: nameInput,
      userStatus,
      osType,
      profile:{
        operationType: operationTypeValue,
        personas: selectedPersonaType ? selectedPersonaType.map(pt => pt.value) : []
      },
      startDate: startDate,
      endDate: endDate,
      generations: previewData.generations ? previewData.generations.map(gen => gen) : [],
      genders: previewData.genders ? previewData.genders.map(gen => gen) : [],
      app: {
        inApps: previewData.app.inApps ? previewData.app.inApps.map(app => app) : [],
        notInApps: previewData.app.notInApps ? previewData.app.notInApps.map(app => app) : []
      },
      locations: previewData.locations ? previewData.locations.map(loc => ({
        lat: loc.lat,
        lon: loc.lon,
        meter: loc.meter
      })) : []
    };

    await dispatch(createAudience(audienceData));
    toggle();
    updateLoading(false);
  };

  const checkNameExists = (name) => {
    const normalizedExistingNames = existingNames.map(name => name.toLowerCase());
    if (normalizedExistingNames.includes(name.toLowerCase())) {
      setPopup('nameError');
      setTimeout(() => setPopup(null), 3000);
      updateLoading(false);
      return true;
    }
    return false;
  };


  const handleStandardSubmit = async (e) => {
    e.preventDefault();
    updateLoading(true);
    const { name, osType, userStatus, operationType } = selectedStandardAudience;

    if (checkNameExists(name)) {
      return;
    }
    await dispatch(createAudience({ name, userStatus, osType, startDate, endDate, profile:{
        operationType: operationType,
      personas: selectedPersonaType ? selectedPersonaType.map(pt => pt.value) : []
    }, target: false }));
    toggle();
    updateLoading(false);
  };

  const handleDefineSubmit = async (e) => {
    e.preventDefault();

    const nameInput = document.getElementById('defineAudienceName').value.trim();
    const definitionText = document.getElementById('defineAudienceText').value.trim();
    const osType = document.getElementById('defineOsTypeSelect').value;
    const userStatus = document.getElementById('defineUserStatusSelect').value;

    if (checkNameExists(nameInput)) {
      return;
    }

    if (existingNames.map(name => name.toLowerCase()).includes(name.toLowerCase())) {
      setPopup('nameError');
      setTimeout(() => setPopup(null), 3000);
      return;
    }

    previewLoading(true);

    try {
      const response = await dispatch(createAudienceDefinePreview({ name: nameInput, userStatus, definition: definitionText, osType }));
      setPreviewData(response.payload);
    } catch (error) {
      console.error('An error occurred:', error);
      setPopup('error');
    } finally {
      previewLoading(false);
    }
  };

  useEffect(() => {
    if (previewData && !previewError) {
      setShowPreviewModal(true);
    }
  }, [previewData]);

  const handleConfirmDefine = (e) => {
    e.preventDefault();
    const nameInput = document.getElementById('defineAudienceName').value.trim();
    const osTypeInput = document.getElementById('defineOsTypeSelect').value;

    if (checkNameExists(nameInput)) {
      return;
    }

    if (existingNames.map(name => name.toLowerCase()).includes(nameInput.toLowerCase())) {
      setPopup('nameError');
      setTimeout(() => setPopup(null), 3000);
      return;
    }

    if (!previewData) {
      setPopup('error');
      setTimeout(() => setPopup(null), 3000);
      return;
    }

    updateLoading(true);

    const audienceData = {
      name: nameInput,
      userStatus: previewData.userStatus || document.getElementById('defineUserStatusSelect').value,
      definition: previewData.definition || document.getElementById('defineAudienceText').value.trim(),
      explain: previewData.explain,
      audienceQuery: previewData.audienceQuery,
      osType: osTypeInput,
    };

    dispatch(defineCreateAudience(audienceData))
      .then((action) => {
        if (action.type.endsWith('fulfilled')) {
          setPopup('success');
        } else {
          setPopup('error');
        }
      })
      .catch((error) => {
        console.error('An error occurred:', error);
        setPopup('error');
      })
      .finally(() => {
        updateLoading(false);
      });

    toggle();
    setShowPreviewModal(false);
  };

  useEffect(() => {
    if (operationStatus === 'succeeded') {
      setPopup('success');
      setTimeout(() => setPopup(null), 3000);
    } else if (operationStatus === 'failed') {
      setPopup('error');
      setTimeout(() => setPopup(null), 3000);
    }
  }, [operationStatus]);

  useEffect(() => {
    if (previewError) {
      if (previewError.includes("CONTAINS_PROPHETY_AND_SEXUALITY")) {
        setPopup('containsProphetyAndSexuality');
      } else {
        setPopup('error');
      }
      setTimeout(() => setPopup(null), 5000);
      dispatch(audienceDefinePreviewSlice.actions.resetPreviewAudienceState());
    }
  }, [previewError]);

  return (
    <>
      <Draggable handle=".modal-header">
        <Modal isOpen={isOpen} toggle={toggle} centered style={{ minWidth: '600px' }} className="modal-dialog modal-lg">
          <ModalHeader toggle={toggle}>Create Audience</ModalHeader>
          <ModalBody>
            {isLoading ? (<Loader />) : (
              <>
                <Nav tabs>
                  {hasFeature("manuel") && (
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => { toggleTab('1'); }}
                      >
                        Create Manually
                      </NavLink>
                    </NavItem>
                  )}
                  {hasFeature("define") && (
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => { toggleTab('2'); }}
                      >
                        Create with AI
                      </NavLink>
                    </NavItem>
                  )}
                  {hasFeature("predefined") && (
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '3' })}
                        onClick={() => { toggleTab('3'); }}
                      >
                        Use Standard
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    <CreateManuallyForm
                      createAudienceName={createAudienceName} setCreateAudienceName={setCreateAudienceName}
                      profileTypeOptions={profileTypeOptions} selectedPersonaType={selectedPersonaType} setSelectedPersonaType={setSelectedPersonaType}
                      startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
                      updateLoading={updateLoading} previewLoading={previewLoading}
                      existingNames={existingNames} setPopup={setPopup}

                    />
                  </TabPane>

                  <TabPane tabId="2">
                    <CreateWithAIForm
                      defineAudienceName={defineAudienceName}
                      setDefineAudienceName={setDefineAudienceName}
                      defineAudienceText={defineAudienceText}
                      setDefineAudienceText={setDefineAudienceText}
                      previewLoading={previewLoading}
                      handleDefineSubmit={handleDefineSubmit}
                      toggle={toggle}
                    />
                  </TabPane>
                  <TabPane tabId="3">
                    <form onSubmit={handleStandardSubmit}>
                      <div className="mb-3">
                        <Select
                          options={standardAudiences.map(sa => ({ value: sa, label: sa.name }))}
                          onChange={(option) => {
                            if (option) {
                              setSelectedStandardAudience(option.value);
                            } else {
                              setSelectedStandardAudience(null);
                            }
                          }}

                          placeholder="Select Standard Audience..."
                          isSearchable={true}
                          isClearable={true}
                        />
                      </div>
                      <div className="hstack gap-2 justify-content-end" style={{ marginTop: 10 }}>
                        <Button type="button" color="light" onClick={toggle}>Close</Button>
                        <Button type="submit" color="success" disabled={!selectedStandardAudience}>
                          Create Audience
                        </Button>
                      </div>
                    </form>
                  </TabPane>
                </TabContent>
              </>
            )}
          </ModalBody>
        </Modal>
      </Draggable>

      {activeTab === '1' && (
        <AudienceManuallyPreviewModal
          isOpen={showPreviewModal}
          toggle={() => setShowPreviewModal(false)}
          isLoading={isLoadingDefine}
          previewData={previewData}
          handleConfirm={handleConfirmSubmit}
        />
      )}

      {activeTab === '2' && (
        <AudiencePreviewModal
          isOpen={showPreviewModal}
          toggle={() => setShowPreviewModal(false)}
          isLoading={isLoadingDefine}
          previewData={previewData}
          defineAudienceName={defineAudienceName}
          handleConfirm={handleConfirmDefine}
        />
      )}

      {popup === 'success' && (
        <div className="alert alert-success alert-dismissible alert-label-icon label-arrow fade show" role="alert" style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 9999,
        }}>
          <i className="ri-check-double-line label-icon"></i><strong>Success</strong> - Create Audience
          <Button type="button" className="btn-close" onClick={closePopup} aria-label="Close"></Button>
        </div>
      )}

      {popup === 'error' && (
        <div className="alert alert-danger alert-dismissible alert-label-icon label-arrow fade show" role="alert" style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 9999,
        }}>
          <i className="ri-error-warning-line label-icon"></i><strong>Error</strong> - Audience Could Not Be Created
          <Button type="button" className="btn-close" onClick={closePopup} aria-label="Close"></Button>
        </div>
      )}

      {popup === 'nameError' && (
        <div className="alert alert-warning alert-dismissible alert-label-icon label-arrow fade show" role="alert" style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 9999,
        }}>
          <i className="ri-alert-line label-icon"></i><strong>Name Error</strong> - This name is already in use. Please choose another name.
          <Button type="button" className="btn-close" onClick={closePopup} aria-label="Close"></Button>
        </div>
      )}

      {popup === 'containsProphetyAndSexuality' && (
        <div className="alert alert-danger alert-dismissible alert-label-icon label-arrow fade show" role="alert" style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 9999,
        }}>
          <i className="ri-alert-line label-icon"></i><strong>Error</strong> - Audience definition contains prohibited content.
          <Button type="button" className="btn-close" onClick={closePopup} aria-label="Close"></Button>
        </div>
      )}
    </>
  );
};

export default AudienceCreate;
